import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { logger } from "../log";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { usePortalApi } from "./portal-api";
import { daily } from "./provider";

const log = logger("map-layers");
const syncModuleId = "map-layers";

export type PortalMapLayerFile = {
  readonly id: string;
  readonly originalName: string;
  readonly mimeType: string;
  readonly size: number;
};

export type PortalWMSSubLayer = {
  readonly name: string;
  readonly title: string;
  readonly opacity?: number;
};

export type PortalMapLayerBase = {
  readonly id: string;
  readonly name: string;
  readonly minZoom?: number;
  readonly maxZoom?: number;
};

export type PortalWMSMapLayer = PortalMapLayerBase & {
  readonly type: "WMS";
  readonly url: string;
  readonly wmsUsername?: string;
  readonly wmsPassword?: string;
  readonly wmsLayers: ReadonlyArray<PortalWMSSubLayer>;
};

export type PortalShapeMapLayer = PortalMapLayerBase & {
  readonly type: "SHAPE";
  readonly file: PortalMapLayerFile;
};

export type PortalMapLayer = PortalWMSMapLayer | PortalShapeMapLayer;

function usePortalMapLayersSyncModule(): SyncModule<
  ReadonlyArray<PortalMapLayer>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Custom WMS/shape map layers are a new-Datenportal-only resource — the
      // legacy portal has no equivalent endpoint, so we simply don't fetch
      // anything for it. The legacy portal continues to serve its custom
      // layers via `license.mapLayers` instead (see `../license`).
      fetchData: useNewApi && baseUrl
        ? () => fetchPortalMapLayers(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchPortalMapLayers(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<PortalMapLayer>> {
  const response = await fetch(`${baseUrl}/api/device/sync/map-layers`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  const mapLayers: PortalMapLayer[] = data.mapLayers ?? [];

  return mapLayers.filter((layer) => {
    if (layer.type !== "WMS" || isAbsoluteHttpUrl(layer.url)) return true;
    log.warn(`Ignoriere WMS-Layer "${layer.id}" mit ungültiger URL: ${layer.url}`);
    return false;
  });
}

function isAbsoluteHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function useMapLayersSync() {
  const module = usePortalMapLayersSyncModule();
  useSyncModuleRegistration(module);
}

export function usePortalMapLayers(): ReadonlyArray<PortalMapLayer> {
  return useSyncData(syncModuleId) ?? [];
}

export function usePortalMapLayerFileDownload(fileId: string): {
  readonly url: string;
  readonly fetchOptions: RequestInit;
} {
  const { portalKey } = useAuthenticated();
  const { baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      url: `${baseUrl}/api/device/sync/map-layers/files/${fileId}/download`,
      fetchOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
    }),
    [baseUrl, fileId, portalKey],
  );
}
